import CheckIcon from '@mui/icons-material/Check';
import ClearIcon from '@mui/icons-material/Clear';
import LockIcon from '@mui/icons-material/Lock';
import WarningIcon from '@mui/icons-material/Warning';
import { Button } from '@mui/material';
import Box from '@mui/material/Box';
import Chip from '@mui/material/Chip';
import GridReal from '@mui/material/Grid';
import IconButton from '@mui/material/IconButton';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import { styled } from '@mui/material/styles';
import type React from 'react';
import { useState } from 'react';

import { type User, UserService } from '~/services/user';
import { Roles } from '~/types/types';
import { usePollingSubscription } from '~/util/use-subscription';

export const UserAdminRoles: Readonly<Roles[]> = [Roles.Administrator];

const Grid = styled(GridReal)({
  padding: '4px',
});

const UserList = (props: any) => {
  const [users, refreshUsers] = usePollingSubscription(UserService.getAll(), [], 30000);
  const [filter, setFilter] = useState('');

  const navigateToUser = (user: User) => {
    props.history.push({
      pathname: `/users/${user.id}`,
      state: { user },
    });
  };

  const resetPassword = (event: React.MouseEvent<HTMLButtonElement>, user: User) => {
    event.stopPropagation();

    if (!confirm('Är du säker?')) {
      return;
    }

    UserService.resetPassword(user).subscribe(() => refreshUsers());
  };

  const removeUser = (event: React.MouseEvent<HTMLButtonElement>, user: User) => {
    event.stopPropagation();

    if (!confirm('Är du säker?')) {
      return;
    }

    UserService.delete(user).subscribe(() => refreshUsers());
  };

  const filterCompare = (user: User) => {
    const userString = JSON.stringify(user).toLocaleLowerCase();
    const lowerFilter = filter.toLocaleLowerCase();

    return lowerFilter.split(' ').reduce((acc, filter) => acc && userString.includes(filter), true);
  };

  return (
    <>
      <Grid item>
        <Box ml={1}>
          <TextField
            variant="standard"
            label="Filtrera"
            onChange={(event) => setFilter(event.target.value)}
            margin="none"
          />
        </Box>
      </Grid>
      <Grid item>
        <Button href="/user/new">Skapa ny användare</Button>
      </Grid>
      <Table size="small">
        <TableHead>
          <TableRow>
            <TableCell>Användarnamn/E-post</TableCell>
            <TableCell>Namn</TableCell>
            <TableCell>Roller</TableCell>
            <TableCell>Senast inloggad</TableCell>
            <TableCell>Senast ändrat lösenord</TableCell>
            <TableCell>Status</TableCell>
            <TableCell>Tvåfaktorsauthentisering</TableCell>
            <TableCell />
          </TableRow>
        </TableHead>
        <TableBody>
          {users.filter(filterCompare).map((user: User, index: number) => {
            return (
              <TableRow hover onClick={() => navigateToUser(user)} style={{ cursor: 'pointer' }} key={index}>
                <TableCell>{user.principal}</TableCell>
                <TableCell>{user.fullName}</TableCell>
                <TableCell>
                  {user.roles.map((role, index) => (
                    <Chip key={index} size="small" label={role.name} />
                  ))}
                </TableCell>
                <TableCell>{user.lastLogin}</TableCell>
                <TableCell>{user.passwordChanged}</TableCell>
                <TableCell>
                  {user.locked && (
                    <IconButton size="small" disabled>
                      <LockIcon />{' '}
                      <Typography color="textSecondary" variant="body2">
                        Låst
                      </Typography>
                    </IconButton>
                  )}
                  {user.tmpPassword && (
                    <IconButton size="small" disabled>
                      <WarningIcon />
                      <Typography color="textSecondary" variant="body2">
                        Återställt lösenord
                      </Typography>
                    </IconButton>
                  )}
                </TableCell>
                <TableCell>
                  {user.use2Factor ? <CheckIcon style={{ color: 'green' }} /> : <ClearIcon style={{ color: 'red' }} />}
                </TableCell>
                <TableCell>
                  <Button size="small" onClick={(event) => resetPassword(event, user)}>
                    Återställ lösenord
                  </Button>
                  <Button size="small" onClick={(event) => removeUser(event, user)} color="secondary">
                    Ta bort
                  </Button>
                </TableCell>
              </TableRow>
            );
          })}
        </TableBody>
      </Table>
    </>
  );
};

export default function UserListPage(props: any) {
  return (
    <>
      <Grid container item xs={12} lg={10} spacing={2}>
        <Grid item>
          <Typography variant="h4">Användarhantering</Typography>
        </Grid>
      </Grid>

      <Grid container item xs={12} lg={10} spacing={2}>
        <Grid item>
          <UserList history={props.history} />
        </Grid>
      </Grid>
    </>
  );
}
